// src/padplanner/clearanceMirror.js
//
// The editor's client mirror of the clearance engine (S5b, design §6 and
// D-S5-9): the distance a drag would leave between features, and the
// redzone halos around wells. It is a preview, never the record. The record
// is the server's run, which every save returns; the mirror's reading is
// replaced by it.
//
// ── PLANAR, IN THE REVISION'S UTM FRAME ─────────────────────────────────────
//
// Shapes are the editor's own (geometry.js localRings, [E, N] meters in the
// revision's frame), and distances are planar in that frame. Not turf: its
// distances are on a sphere, about 0.2 ft off on the fixture's east-west pair,
// outside the 0.05 ft the fixture allows. Planar 32613 reads the fixture's
// four pairs within 0.016 ft of the server's geography distance
// (clearanceMirror.test.js holds every pair to 0.05 ft).
//
// What is measured is what the engine measures (D-S5-5): a well-linked
// feature from its anchor (the wellhead center), any other footprint or zone
// from its outline (a zone's is its 64-segment buffer), a line (156) from
// its centerline; overlapping shapes, or a line that crosses or enters one,
// are 0 apart. A line is selected only by a selector that names its layer or
// category "line" (ruleVocabulary.js selectorMatches), as the engine does.
//
// ── THE DEFER BAND ──────────────────────────────────────────────────────────
//
// Within DEFER_WITHIN_FT (0.1 ft) of a limit, planar and geography can land
// on different sides of it (the fixture's near_limit_below reads 99.998 in
// geography and 100.014 planar against a 100 ft min). There the reading is
// "≈ — checked on save", not a verdict.
//
// ── WHAT A LIVE READING COVERS ──────────────────────────────────────────────
//
// The dragged features against the distance rules each is a subject or an
// object of: min_distance in either role, max_distance as the subject (the
// nearest object). One reading is shown, the nearest pair that violates its
// rule or sits in the band. inside_perimeter and no_overlap are left to the
// save's run.
import { localRings } from "../lib/footprint-editor/geometry.js";
import { ftToM, mToFt } from "../lib/units.js";
import { DEFER_WITHIN_FT } from "./checksVocabulary";
import { selectorMatches, featureFacts } from "./ruleVocabulary";

const MEASURED_KINDS = new Set(["footprint", "zone", "line"]);
const HALO_SEGMENTS = 64;

/* ── shapes ──────────────────────────────────────────────────────────────── */

function boxOf(points) {
  let xmin = Infinity, ymin = Infinity, xmax = -Infinity, ymax = -Infinity;
  for (const [x, y] of points) {
    if (x < xmin) xmin = x; if (x > xmax) xmax = x;
    if (y < ymin) ymin = y; if (y > ymax) ymax = y;
  }
  return { xmin, ymin, xmax, ymax };
}

/**
 * What the engine measures for a feature, in frame meters: {point} for a
 * well-linked one (its anchor), {path} for a line, {rings} for any other
 * footprint or zone, or null (nothing to draw yet). With a box for the
 * prefilter.
 */
export function measuredShape(feature, template, frame) {
  if (!frame || !MEASURED_KINDS.has(feature.kind)) return null;
  if (feature.kind === "line") {
    const c = feature.line_geom?.coordinates;
    if (!(c?.length >= 2)) return null;
    const path = c.map((p) => frame.toLocal(p));
    return { path, box: boxOf(path) };
  }
  if (feature.well_api10 && feature.anchor?.coordinates) {
    const p = frame.toLocal(feature.anchor.coordinates);
    return { point: p, box: { xmin: p[0], ymin: p[1], xmax: p[0], ymax: p[1] } };
  }
  const rings = localRings(feature, template, frame);
  if (!rings?.length) return null;
  return { rings, box: boxOf(rings[0]) };
}

function boxGap(a, b) {
  const dx = Math.max(0, a.xmin - b.xmax, b.xmin - a.xmax);
  const dy = Math.max(0, a.ymin - b.ymax, b.ymin - a.ymax);
  return Math.hypot(dx, dy);
}

function closestOnSegment(p, a, b) {
  const dx = b[0] - a[0], dy = b[1] - a[1];
  const len2 = dx * dx + dy * dy;
  const t = len2 ? Math.max(0, Math.min(1, ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / len2)) : 0;
  return [a[0] + t * dx, a[1] + t * dy];
}

function segmentsCross(a, b, c, d) {
  const o = (p, q, r) => (q[0] - p[0]) * (r[1] - p[1]) - (q[1] - p[1]) * (r[0] - p[0]);
  const d1 = o(c, d, a), d2 = o(c, d, b), d3 = o(a, b, c), d4 = o(a, b, d);
  return ((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0)) && ((d3 > 0 && d4 < 0) || (d3 < 0 && d4 > 0));
}

function inRing(p, ring) {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i], [xj, yj] = ring[j];
    if ((yi > p[1]) !== (yj > p[1]) && p[0] < ((xj - xi) * (p[1] - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}

/** Inside the outer ring and in none of the holes. */
function inShape(p, rings) {
  if (!inRing(p, rings[0])) return false;
  for (let i = 1; i < rings.length; i++) if (inRing(p, rings[i])) return false;
  return true;
}

const segmentsOf = (s) => (s.point ? [[s.point, s.point]]
  : s.path ? s.path.slice(1).map((p, i) => [s.path[i], p])
  : s.rings.flatMap((r) => r.slice(1).map((p, i) => [r[i], p])));
const verticesOf = (s) => (s.point ? [s.point] : s.path ? s.path : s.rings.flatMap((r) => r));

/**
 * The planar distance between two measured shapes, in meters, and the
 * closest points ([E, N] each): the shortest line, as ST_ShortestLine draws
 * the witness. 0 when they overlap or one holds the other.
 */
export function shapeDistance(a, b) {
  if (b.rings) for (const p of verticesOf(a)) if (inShape(p, b.rings)) return { m: 0, pa: p, pb: p };
  if (a.rings) for (const p of verticesOf(b)) if (inShape(p, a.rings)) return { m: 0, pa: p, pb: p };
  const sa = segmentsOf(a), sb = segmentsOf(b);
  let best = { m: Infinity, pa: null, pb: null };
  for (const [p0, p1] of sa) {
    for (const [q0, q1] of sb) {
      if (segmentsCross(p0, p1, q0, q1)) return { m: 0, pa: p0, pb: p0 };
      for (const [p, s0, s1, pFirst] of [[p0, q0, q1, true], [p1, q0, q1, true], [q0, p0, p1, false], [q1, p0, p1, false]]) {
        const c = closestOnSegment(p, s0, s1);
        const m = Math.hypot(p[0] - c[0], p[1] - c[1]);
        if (m < best.m) best = pFirst ? { m, pa: p, pb: c } : { m, pa: c, pb: p };
      }
    }
  }
  return best;
}

/** Feet between two features as the engine measures them, planar in the frame. */
export function featureDistanceFt(f, g, templateOf, frame) {
  const a = measuredShape(f, templateOf(f), frame), b = measuredShape(g, templateOf(g), frame);
  if (!a || !b) return null;
  return mToFt(shapeDistance(a, b).m);
}

/* ── the live reading ─────────────────────────────────────────────────────── */

/**
 * Shapes cached by feature object: the editor replaces a feature's object on
 * every edit, so an unchanged feature keeps its entry for the whole drag.
 * One cache per (frame, templates), made by the caller.
 */
export function shapeCache(templateOf, frame) {
  const shapes = new WeakMap();
  return (f) => {
    if (!shapes.has(f)) shapes.set(f, measuredShape(f, templateOf(f), frame));
    return shapes.get(f);
  };
}

/**
 * The reading to show while dragging: the nearest pair, among the dragged
 * features' distance rules, that violates its rule or sits within the defer
 * band, or null when every pair is clear.
 *
 * @param {object} p
 * @param {object[]} p.moved      the dragged features as the drag would leave them
 * @param {object[]} p.features   the editor's features (the dragged ones are replaced by `moved`)
 * @param {(f: object) => object|null} p.templateOf
 * @param {object} p.frame        the revision's frame (frame.js)
 * @param {object[]} p.rules      the published set's rules
 * @param {(f: object) => object|null} [p.shapeOf]  a shapeCache, for a drag's many moves
 * @returns {null | {ft, rule, deferred, subjectId, objectId, from: number[], to: number[]}}  from/to are [lng, lat]
 */
export function liveReading({ moved, features, templateOf, frame, rules, shapeOf = null }) {
  if (!frame || !moved?.length || !rules?.length) return null;
  const shape = shapeOf ?? ((f) => measuredShape(f, templateOf(f), frame));
  const movedById = new Map(moved.map((f) => [f.id, f]));
  const all = features.map((f) => movedById.get(f.id) ?? f).filter((f) => MEASURED_KINDS.has(f.kind));
  const facts = new Map(all.map((f) => [f.id, featureFacts(f, templateOf(f))]));
  let best = null;
  const consider = (rule, m, ms, other, subjectIsMoved) => {
    const os = shape(other);
    if (!os) return null;
    const res = shapeDistance(ms, os);
    const ft = mToFt(res.m);
    return { ft, rule, subjectId: subjectIsMoved ? m.id : other.id, objectId: subjectIsMoved ? other.id : m.id, pa: res.pa, pb: res.pb };
  };
  const keep = (c) => {
    if (!c) return;
    const limit = Number(c.rule.limit_ft);
    const deferred = Math.abs(c.ft - limit) < DEFER_WITHIN_FT;
    const violates = c.rule.relation === "min_distance" ? c.ft < limit : c.ft > limit;
    if (!deferred && !violates) return;
    if (!best || c.ft < best.ft) best = { ...c, deferred };
  };

  for (const m of moved) {
    if (!MEASURED_KINDS.has(m.kind)) continue;
    const ms = shape(m);
    if (!ms) continue;
    const mf = facts.get(m.id) ?? featureFacts(m, templateOf(m));
    for (const rule of rules) {
      if (rule.relation !== "min_distance" && rule.relation !== "max_distance") continue;
      const limit = Number(rule.limit_ft);
      const reach = ftToM(limit + DEFER_WITHIN_FT);
      const asSubject = selectorMatches(rule.subject_selector, mf);
      const asObject = rule.relation === "min_distance" && selectorMatches(rule.object_selector, mf);
      if (!asSubject && !asObject) continue;
      if (rule.relation === "min_distance") {
        for (const o of all) {
          if (o.id === m.id) continue;
          const of = facts.get(o.id);
          const pairs = (asSubject && selectorMatches(rule.object_selector, of)) || (asObject && selectorMatches(rule.subject_selector, of));
          if (!pairs) continue;
          const os = shape(o);
          if (!os || boxGap(ms.box, os.box) > reach) continue;
          keep(consider(rule, m, ms, o, asSubject && selectorMatches(rule.object_selector, of)));
        }
      } else {
        // max_distance, the dragged feature as the subject: its nearest object.
        let nearest = null;
        for (const o of all) {
          if (o.id === m.id || !selectorMatches(rule.object_selector, facts.get(o.id))) continue;
          const os = shape(o);
          if (!os || (nearest && boxGap(ms.box, os.box) >= nearest.ft * 0.3048)) continue;
          const c = consider(rule, m, ms, o, true);
          if (c && (!nearest || c.ft < nearest.ft)) nearest = c;
        }
        keep(nearest);
      }
    }
  }
  if (!best) return null;
  return {
    ft: best.ft, rule: best.rule, deferred: best.deferred, subjectId: best.subjectId, objectId: best.objectId,
    from: frame.toLngLat(best.pa), to: frame.toLngLat(best.pb),
  };
}

/* ── redzones ─────────────────────────────────────────────────────────────── */

/**
 * The redzone halos (D-S5-3: redzones are rule-derived). For each well-linked
 * feature, the published min_distance rules it is a subject or an object of;
 * their union around its anchor is the circle of the largest limit, built in
 * the frame (64 segments) and projected back. Returns the circles as
 * polygons (the fill) and the union's outline across wells: each circle's
 * arcs that lie inside no other circle, so overlapping halos read as one
 * shape, as mockup 4 draws them.
 */
export function redzones({ features, templateOf, frame, rules }) {
  const empty = { fill: { type: "FeatureCollection", features: [] }, outline: { type: "FeatureCollection", features: [] }, circles: [] };
  if (!frame || !rules?.length) return empty;
  const minRules = rules.filter((r) => r.relation === "min_distance");
  if (!minRules.length) return empty;
  const circles = [];
  for (const f of features) {
    if (!f.well_api10 || !f.anchor?.coordinates || !MEASURED_KINDS.has(f.kind)) continue;
    const facts = featureFacts(f, templateOf(f));
    let limit = 0;
    for (const r of minRules) {
      if (selectorMatches(r.subject_selector, facts) || selectorMatches(r.object_selector, facts)) limit = Math.max(limit, Number(r.limit_ft));
    }
    if (!(limit > 0)) continue;
    circles.push({ id: f.id, center: frame.toLocal(f.anchor.coordinates), r: ftToM(limit), limit_ft: limit });
  }
  const fill = [];
  const outline = [];
  for (const c of circles) {
    const ring = [];
    for (let i = 0; i <= HALO_SEGMENTS; i++) {
      const t = (i % HALO_SEGMENTS) * (2 * Math.PI / HALO_SEGMENTS);
      ring.push([c.center[0] + c.r * Math.cos(t), c.center[1] + c.r * Math.sin(t)]);
    }
    fill.push({ type: "Feature", properties: { id: c.id, limit_ft: c.limit_ft }, geometry: { type: "Polygon", coordinates: [ring.map((p) => frame.toLngLat(p))] } });
    const others = circles.filter((o) => o !== c);
    const outside = (p) => others.every((o) => Math.hypot(p[0] - o.center[0], p[1] - o.center[1]) >= o.r);
    let run = [];
    const runs = [];
    for (const p of ring) {
      if (outside(p)) run.push(p);
      else { if (run.length > 1) runs.push(run); run = []; }
    }
    if (run.length > 1) runs.push(run);
    // The ring is closed: a run that ends at the last vertex continues into the first.
    if (runs.length > 1 && outside(ring[0]) && outside(ring[ring.length - 1])) {
      const tail = runs.pop();
      runs[0] = [...tail, ...runs[0].slice(1)];
    }
    for (const r of runs) {
      outline.push({ type: "Feature", properties: { id: c.id }, geometry: { type: "LineString", coordinates: r.map((p) => frame.toLngLat(p)) } });
    }
  }
  return { fill: { type: "FeatureCollection", features: fill }, outline: { type: "FeatureCollection", features: outline }, circles };
}
